import { useEffect, useState } from 'react';
import { GroupBase, components as reactSelectComponents } from 'react-select';
import Select, { Props } from 'react-select/base';
import React from 'react';

import { Flex } from '@reckon-web/flex';
import { XIcon } from '@reckon-web/icon';
import { LoadingDots } from '@reckon-web/loading';
import { useTheme } from '@reckon-web/theme';

import { defaultOptionRenderer } from './option-renderers';
import { AutocompleteProps, BaseOption } from './types';

function useHasValueBeenConstantForMs(value: any, ms: number) {
  const [hasValueBeenConstant, setHasValueBeenConstant] = useState(false);

  useEffect(() => {
    setHasValueBeenConstant(false);
    const id = setTimeout(() => setHasValueBeenConstant(true), ms);
    return () => clearTimeout(id);
  }, [value, ms]);

  return hasValueBeenConstant;
}

type AutocompleteState =
  | { type: 'no-value' }
  | { type: 'loading' }
  | { type: 'loaded'; options: BaseOption[] };

function useAutocompleteState(
  inputValue: string,
  loadOptions: (inputValue: string) => Promise<any[]>
): AutocompleteState {
  const [optionsState, setOptionsState] = useState<{
    loadOptions: any;
    inputValue: string;
    options: BaseOption[];
  }>({ options: [], loadOptions: null, inputValue: '' });
  const isValueStable = useHasValueBeenConstantForMs(inputValue, 200);

  useEffect(() => {
    let shouldSet = true;
    if (isValueStable && inputValue) {
      // TODO: what should we do if there is an error?
      loadOptions(inputValue).then((options) => {
        if (shouldSet) {
          setOptionsState({ options, loadOptions, inputValue });
        }
      });
    }
    return () => {
      shouldSet = false;
    };
  }, [loadOptions, isValueStable, inputValue]);

  if (inputValue === '') {
    return {
      type: 'no-value',
    };
  }

  if (
    (optionsState.inputValue !== inputValue ||
      optionsState.loadOptions !== loadOptions) &&
    isValueStable
  ) {
    return {
      type: 'loading',
    };
  }

  return {
    type: 'loaded',
    options: optionsState.options,
  };
}

export function createComponents<Option extends BaseOption>(): Props<
  Option,
  false,
  GroupBase<Option>
>['components'] {
  return {
    ClearIndicator(props) {
      return (
        <reactSelectComponents.ClearIndicator {...props}>
          <XIcon size="small" />
        </reactSelectComponents.ClearIndicator>
      );
    },

    LoadingIndicator() {
      return null;
    },

    DropdownIndicator() {
      return null;
    },

    IndicatorSeparator() {
      return null;
    },

    LoadingMessage() {
      return (
        <Flex justifyContent="center" marginY="xlarge">
          <LoadingDots label="Loading options" />
        </Flex>
      );
    },
  };
}

export function Autocomplete<Option extends BaseOption>({
  renderOption = defaultOptionRenderer,
  noOptionsMessage = defaultNoOptionsMessage,
  ...props
}: AutocompleteProps<Option>) {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const state = useAutocompleteState(inputValue, props.loadOptions);
  const components = createComponents<Option>();

  return (
    <Select
      aria-describedby={props['aria-describedby']}
      autoFocus={props.autoFocus}
      components={components}
      filterOption={() => true}
      formatOptionLabel={renderOption as any}
      inputId={props.id}
      inputValue={inputValue}
      isClearable
      isLoading={state.type === 'loading'}
      menuIsOpen={isOpen && state.type !== 'no-value'}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
      noOptionsMessage={noOptionsMessage}
      onChange={(option: any) => props.onChange(option)}
      onInputChange={(value) => setInputValue(value)}
      onMenuClose={() => setIsOpen(false)}
      onMenuOpen={() => setIsOpen(true)}
      options={state.type === 'loaded' ? state.options : []}
      placeholder={props.placeholder ?? 'Search...'}
      styles={useStyles(props)}
      value={props.value}
      onBlur={() => setInputValue('')}
    />
  );
}

const defaultNoOptionsMessage = () => 'No matching results.';

// Styles
// ------------------------------
type StyleProps<Option extends BaseOption> = Pick<
  AutocompleteProps<Option>,
  'id' | 'value' | 'invalid'
>;

function renderSearchIconSVG(color: string = 'currentColor') {
  return `
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="${color}" stroke-width="2" viewBox="0 0 24 24">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  `;
}

function useStyles<Option extends BaseOption>(
  props: StyleProps<Option>
): Props<Option, false, GroupBase<Option>>['styles'] {
  const { elevation, palette, shadow } = useTheme();

  return {
    // CONTROL
    control: (base, state) => ({
      ...base,
      backgroundColor: props.invalid
        ? palette.formInput.backgroundInvalid
        : palette.formInput.background,
      borderRadius: 8,
      borderColor: state.isFocused
        ? palette.formInput.borderFocused
        : 'transparent',
      boxShadow: state.isFocused
        ? `0 0 0 2px ${palette.global.focusRing}`
        : undefined,
      color: props.invalid
        ? palette.formInput.textInvalid
        : palette.formInput.text,
      minHeight: 40,
      transition: undefined,
      cursor: 'text',
      ':hover': {
        borderColor: state.isFocused ? undefined : palette.formInput.border,
      },
      //Any override to custom component broke input focusing, hence creating search icon with css
      '::before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        left: 12,
        top: '50%',
        width: 16,
        height: 16,
        transform: 'translateY(-50%)',
        backgroundImage: `url("data:image/svg+xml;utf8,${encodeURIComponent(
          renderSearchIconSVG(palette.formInput.textPlaceholder)
        )}")`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '16px 16px',
      },
    }),
    clearIndicator: (base) => ({ ...base, padding: 11 }),
    valueContainer: (base) => ({
      ...base,
      paddingLeft: 38,
    }),
    input: (base) => ({
      ...base,
      color: props.invalid
        ? palette.formInput.textInvalid
        : palette.formInput.text,
    }),
    singleValue: (base) => ({ ...base }),
    placeholder: (base) => ({
      ...base,
      color: palette.formInput.textPlaceholder,
    }),

    // MENU
    noOptionsMessage: (base) => ({
      ...base,
      fontSize: 14,
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: palette.background.dialog,
      borderRadius: 8,
      boxShadow: shadow.medium,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: elevation.toast,
    }),
    menuList: (base) => ({
      ...base,
      paddingBottom: 8,
      paddingTop: 8,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused
        ? palette.menuItem.backgroundFocused
        : undefined,
      color: state.isFocused
        ? palette.menuItem.textFocused
        : palette.menuItem.text,

      ':active': {
        backgroundColor: palette.menuItem.backgroundPressed,
        color: palette.menuItem.textPressed,
      },
    }),
  };
}
